import React, { useMemo, useState } from "react";
import { Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import ErrorAlert from "components/errors/ErrorAlert";
import useAppConfig from "hooks/access-control/useAppConfig";

const flattenConfig = (value, prefix = "") => {
  if (value === null || value === undefined) {
    return [];
  }

  if (typeof value !== "object" || Array.isArray(value)) {
    return [{ key: prefix || "value", value }];
  }

  return Object.entries(value).flatMap(([childKey, childValue]) => {
    const nextKey = prefix ? `${prefix}.${childKey}` : childKey;
    if (
      childValue &&
      typeof childValue === "object" &&
      !Array.isArray(childValue)
    ) {
      return flattenConfig(childValue, nextKey);
    }
    return [{ key: nextKey, value: childValue }];
  });
};

const maskValue = value => {
  if (value === null || value === undefined) {
    return "Not set";
  }

  if (typeof value === "boolean" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `${value.length} item(s)`;
  }

  const textValue = String(value);
  if (textValue.length <= 8) {
    return "****";
  }

  return `${textValue.slice(0, 4)}...${textValue.slice(-4)}`;
};

const AppConfigPage = () => {
  const { appConfig, loading, error, refresh } = useAppConfig();
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  const configRows = useMemo(() => flattenConfig(appConfig), [appConfig]);
  const formattedJson = useMemo(() => {
    if (!appConfig) return "No app config data available.";

    return JSON.stringify(appConfig, null, 2);
  }, [appConfig]);

  return (
    <>
      <WidgetSectionTitle
        icon="sliders-h"
        title="App Config"
        subtitle="Read-only configuration from the Olitask service."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <Row className="mb-3 align-items-center gx-3">
        <Col xs="auto">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="sync"
            transform="shrink-3"
            onClick={refresh}
            disabled={loading}
          >
            Refresh
          </IconButton>
        </Col>
        {loading && (
          <Col xs="auto" className="d-flex align-items-center">
            <Spinner animation="border" size="sm" variant="success" />
            <span className="ms-2" style={{ fontSize: 12 }}>
              Loading...
            </span>
          </Col>
        )}
      </Row>

      <ErrorAlert error={error} />

      <Card className="shadow-none border">
        <Card.Header className="bg-light">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="text-muted small">
              Source: Olitask Service Configuration
            </span>
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              onClick={() => setShowJsonPreview(prev => !prev)}
            >
              {showJsonPreview ? "Hide JSON" : "Show JSON"}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!configRows.length ? (
            <p className="mb-0 text-muted small">No app config data available.</p>
          ) : (
            <Table responsive size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Config Key</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {configRows.map(row => (
                  <tr key={row.key}>
                    <td className="text-muted">{row.key}</td>
                    <td
                      style={{
                        fontFamily:
                          "SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace",
                        fontSize: 12,
                      }}
                    >
                      {maskValue(row.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {showJsonPreview && (
            <pre
              className="mb-0 mt-3 p-3 bg-light border rounded"
              style={{
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {formattedJson}
            </pre>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default AppConfigPage;
